import Styles from './styles.module.css';
import React, { useState } from 'react';
import DefaultInput1 from '../../../package/1-input/index';
import DefaultTextarea1 from '../../../package/1-textarea/index';
import { v4 as uuid } from 'uuid';
import { ImCross } from "react-icons/im";
import { IoImageOutline } from "react-icons/io5";
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import ErrorMessage from '../../../package/error/index';
import { strongData } from 'src/type';
import DefaultImage from './../../../../image/default/account.png';

type RecruitPhotoProps = {
  documentId: string;
  setChangeData: React.Dispatch<React.SetStateAction<boolean>>;
  strong: strongData;
  setStrong: React.Dispatch<React.SetStateAction<strongData>>;
};

const ITEM_KEYS = ['item1', 'item2', 'item3'] as const;
type ItemKey = typeof ITEM_KEYS[number];

const RecruitPhoto: React.FC<RecruitPhotoProps> = ({
  documentId,
  setChangeData,
  strong,
  setStrong
}) => {
  
  const [ errorMessage, setErrorMessage ] = useState('');
  const [ banner, setBanner ] = useState(false);

  const [ imageNames, setImageNames ] = useState<string[][]>([
    strong.item1.images,
    strong.item2.images,
    strong.item3.images,
  ]);

  const [ imagePaths, setImagePaths ] = useState<(string | File)[][]>([
    strong.item1.images,
    strong.item2.images,
    strong.item3.images,
  ]);

  const getItem = (i: number) => strong[`item${i + 1}` as ItemKey];

  const updateItem = (i: number, updater: (item: any) => any) => {
    setStrong(prev => {
      const key = `item${i + 1}` as ItemKey;
      return {
        ...prev,
        [key]: updater(prev[key]),
      };
    });
  };

  function validateCompanyData(data: strongData): string | null {
    for (let i = 1; i <= 3; i++) {
      const item = data[`item${i}` as ItemKey];
      if (item.images.length === 0) return `${i}.アピールの写真を1枚以上添付してください。`;
      if (!item.title) return `${i}.アピールのタイトルを入力してください。`;
      if (!item.desc) return `${i}.アピールの詳細を入力してください。`;
    }
    return null;
  }

  function check() {
    setErrorMessage('');
    window.scrollTo(0, 0);
    const error = validateCompanyData(strong);
    if (error) {
      setErrorMessage(error);
      return;
    }
    save(true);
  }

  const [ uploading, setUploading ] = useState(false);

  async function save(isCompleted: boolean) {

    setUploading(true);

    const db = getFirestore();
    const storage = getStorage();

    const replaced = [0, 1, 2].map(i =>
      strong[`item${i + 1}` as ItemKey].images.map((img, idx) =>
        img.startsWith("blob:") ? imageNames[i][idx] : img
      )
    );

    const dataToSend = {
      ...strong,
      item1: { ...strong.item1, images: replaced[0] },
      item2: { ...strong.item2, images: replaced[1] },
      item3: { ...strong.item3, images: replaced[2] },
      isCompleted,
      time: new Date(),
    };

    let ok = false;

    try {

      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < imagePaths[i].length; j++) {
          const path = imagePaths[i][j];
          if (path instanceof File) {
            const name = imageNames[i][j];
            const fileRef = ref(storage, `/recruitImage${i + 1}/${name}`);
            await uploadBytes(fileRef, path);
          }
        }
      }

      await updateDoc(doc(db, "recruits", documentId), {
        strong: dataToSend,
      });

      setChangeData(false);
      ok = true;

    } catch (e) {
      console.error(e);
      setErrorMessage('保存に失敗しました。時間をおいてもう一度お試しください。');
    } finally {
      setUploading(false);
      // 保存できたときだけ「保存しました」を出す(失敗時に出ていたバグの修正)。
      if (ok) {
        setBanner(true);
        setTimeout(() => setBanner(false), 1500);
      }
    }
  }

  const handleAddImage = (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    // companyIdが会社ドキュメントIDと一致しない古い会社でもStorage側で
    // owner判定できるよう、ファイル名にdocumentId(recruitId)を埋め込む
    // (storage.rules の isOwningCompanyForRecruitAsset() が前提とする形式)。
    const uid = `${documentId}_${uuid()}`;

    updateItem(i, item => ({
      ...item,
      images: [...item.images, URL.createObjectURL(file)],
    }));

    const setNames = [...imageNames];
    setNames[i] = [...(setNames[i] || []), uid];
    setImageNames(setNames);

    const setPaths = [...imagePaths];
    setPaths[i] = [...(setPaths[i] || []), file];
    setImagePaths(setPaths);

    setChangeData(true);
  };

  // 既存スロットの差し替え
  const handleReplaceImage = (i: number, index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const uid = `${documentId}_${uuid()}`;

    updateItem(i, item => ({
      ...item,
      images: item.images.map((img: string, idx: number) => (idx === index ? URL.createObjectURL(file) : img)),
    }));

    setImageNames(prev => {
      const copy = prev.map(a => [...a]);
      copy[i][index] = uid;
      return copy;
    });

    setImagePaths(prev => {
      const copy = prev.map(a => [...a]);
      copy[i][index] = file;
      return copy;
    });

    setChangeData(true);
  };

  const handleRemoveImage = (i: number, index: number) => {
    updateItem(i, item => ({
      ...item,
      images: item.images.filter((_: any, idx: any) => idx !== index),
    }));

    setImageNames(prev => {
      const copy = [...prev];
      copy[i] = copy[i].filter((_, idx) => idx !== index);
      return copy;
    });

    setImagePaths(prev => {
      const copy = [...prev];
      copy[i] = copy[i].filter((_, idx) => idx !== index);
      return copy;
    });

    setChangeData(true);
  };

  return (
    <>
      {errorMessage && <ErrorMessage message={errorMessage} />}
      {banner && <div className={Styles.banner}>保存しました。</div>}

      <div className={Styles.title_edit}>
        <h4>写真で見る</h4>
        <div className={Styles.title_edit_btn}>
          <button className={Styles.save_box_now} onClick={() => save(false)}>
            一時保存
          </button>
          <button className={Styles.save_box_save} onClick={check}>
            保存
          </button>
        </div>
      </div>

      {[0, 1, 2].map((i) => {
        type ItemKey = "item1" | "item2" | "item3";

        const key: ItemKey = `item${i + 1}` as ItemKey;
        const item = strong[key];

        return (
          <div key={i} className={Styles.test_box}>
            <p className={Styles.store_input_photo_title}>アピール写真（{item.images.length}枚/3枚中）{i < 2 ? ' ※' : ''}</p>

            <div className={Styles.photoGrid}>
              {[0, 1, 2].map((slot) => {
                const data = item.images[slot];

                if (data !== undefined) {
                  return (
                    <div key={slot} className={Styles.photoSlot}>
                      <label className={`${Styles.photoBox} ${Styles.photoBox_filled}`}>
                        <img
                          className={Styles.photoPreview}
                          src={ data.startsWith('blob:') ? data : `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/recruitImage${i + 1}%2Fresize_images%2F${data}_500x500?alt=media&token=` }
                          alt=""
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = DefaultImage;
                          }}
                        />
                        <input
                          type="file"
                          accept="image/*"
                          className={Styles.hiddenInput}
                          onChange={handleReplaceImage(i, slot)}
                        />
                      </label>
                      <button
                        type="button"
                        className={Styles.photoRemove}
                        aria-label="削除"
                        onClick={() => handleRemoveImage(i, slot)}
                      >
                        <ImCross />
                      </button>
                    </div>
                  );
                }

                return (
                  <div key={slot} className={Styles.photoSlot}>
                    <label className={Styles.photoBox}>
                      <span className={Styles.photoPlaceholder}>
                        <IoImageOutline className={Styles.photoPlaceholderIcon} />
                        写真を追加
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className={Styles.hiddenInput}
                        onChange={handleAddImage(i)}
                      />
                    </label>
                  </div>
                );
              })}
            </div>

            <DefaultInput1
              error={false}
              title="タイトル ※"
              length={30}
              value={item.title}
              setValue={(val: string) => {
                const key: ItemKey = `item${i + 1}` as ItemKey;

                setStrong(prev => ({
                  ...prev,
                  [key]: {
                    ...prev[key],
                    title: val,
                  },
                }));

                setChangeData(true);
              }}
              desc=""
              place=""
              hint={{ title: "", desc: "" }}
              setHintText={() => {}}
            />

            <DefaultTextarea1
              error={false}
              title="詳細 ※"
              length={100}
              value={item.desc}
              setValue={(val: string) => {
                const key: ItemKey = `item${i + 1}` as ItemKey;

                setStrong(prev => ({
                  ...prev,
                  [key]: {
                    ...prev[key],
                    desc: val,
                  },
                }));

                setChangeData(true);
              }}
              desc=""
              place=""
              hint={{ title: "", desc: "" }}
              setHintText={() => {}}
            />
          </div>
        );
      })}

      {uploading && (
        <div className={Styles.lockOverlay}>
          <div className={Styles.spinner} />
          <p>アップロード中...</p>
        </div>
      )}
    </>
  );
};

export default RecruitPhoto;

